/* global angular, _*/
import * as ng from 'angular'
import * as _ from 'lodash-es';
import {ISelectionManager, ISelectedItem} from '../types/selectionManager'
import AppConstants from '../gorillaboy/AppConstants';


/**
 * @ngdoc service
 * @name FileManagerFactory
 *
 * @description
 * FileManager is an abstract representation of a viewing and selecting the content of a folder
 * E.g. Selecting and unselecting files and folders. This should help any UI implementation with
 * state management and focus on implementation-specific details (e.g. list view vs thumbnail view)
 */
angular.module('GorillaBoy.FileBrowser')
.factory('FileManagerFactory', [
    '$rootScope',
    '$q',
    '$log',
    '$window',
    '$translate',
    '$filter',
    'FileService',
    'DocumentService',
    'SystemTags',
    'SignalProvider',
    'ResourceService',
    'FeatureManagerService',
    'StringHashSet',
    'SortService',
    'FavoritesService',
    'UserCollectionService',
    FileManagerFactory
]);


function FileManagerFactory ($rootScope, $q: ng.IQService, $log, $window, $translate, $filter, fileService, DocumentService, SystemTags, SignalProvider,
    resourceService, featureManager, StringHashSet, SortService, FavoritesService, UserCollectionService) {
    var preFetchedSharedWithMeData = null,
        userInfoContext = null,
        rootFolderId = null,
        recoveryFolderId = null,
        noteFolderId = null,
        preFetchSharedWithMePendingPromise = null;

    function preFetchSharedWithMe() {
        preFetchSharedWithMePendingPromise = fileService.getByTag(SystemTags.SHARED_WITH_ME)
        .then(function (results) {
            preFetchedSharedWithMeData = results;
            return results;
        })
        .finally(function() {
            preFetchSharedWithMePendingPromise = null;
        });
    }

    function ensureUserContext () {
        if (!userInfoContext) {
            throw {error: 'No userContext set on FileManager'};
        }
    }

    function setUserContext (userInfo) {
        var requiredProperties = ['Id', 'RootFolderId', 'RecycleBinFolderId'],
            i;

        // Ensure we have the data we need from this object.
        for (i = 0; i < requiredProperties.length; i += 1) {
            if (!userInfo.hasOwnProperty(requiredProperties[i])) {
                throw {error: 'Missing property "' + requiredProperties[i] + '" on user object'};
            }
        }
        userInfoContext = userInfo;
        fileService.setUserContext(userInfoContext);
        rootFolderId = userInfo.RootFolderId;
        noteFolderId = !userInfo.NoteInfo || angular.isUndefined(userInfo.NoteInfo.NoteFolderId)? null : userInfo.NoteInfo.NoteFolderId;
        recoveryFolderId = angular.isUndefined(userInfo.RecoveryFolderId)? null : userInfo.RecoveryFolderId;
    }

    function getOwnerId(docOrFolder) {
        // The resource collection v2 provides a list of documents and folders, each of which contains OwnerId but no Owner metadata.
        // The v1 has an Owner metadata but null OwnerId.
        return docOrFolder.OwnerId || (docOrFolder.Owner && docOrFolder.Owner.Id) || docOrFolder.CreatorId;
    }

    class FileManager implements ISelectionManager {
        private permissions = AppConstants.Permissions;
        private viewTypes = AppConstants.ViewTypes;
        private resourceStatus = AppConstants.ResourceStatus;
        private sortOrder = SortService.CONSTANTS.ORDER;
        private SORT_DIRECTION_ASCENDING = 1;
        private SORT_DIRECTION_DESCENDING = -1;
        private currentFolderId = null;
        private currentFolder = null;
        private inFavorites = false;
        private inTrash = false;
        private inRecoveryFolderRoot = false;
        private inSharedWithMeRoot = false;
        private inSharedByMeRoot = false;
        private inNoteFolderRoot = false;
        private inOutForReview = false;
        private inAwaitingMyFeedback = false;
        private currentSortBy = null;
        private currentSortDirection = this.sortOrder.ASCENDING;
        private currentFolderFolders = [];
        private currentFolderFiles = [];
        private pagingMarker = null;
        private currentFolderFolderMap = {};
        private currentFolderFileMap = {};
        private currentFolderRights = null;
        private currentFolderPrincipals = null;
        private currentSharedByMeResourceFilter = {
                startTime : null,
                endTime : null,
                resourceCategory : null
            };
        private currentFolderSharedLinkScope = null;
        private currentFolderSharedLinkUrl = null;

        private selectedFoldersSet = new StringHashSet();
        private selectedFilesSet = new StringHashSet();
        private selectedItemCount = 0;
        private selectedFileCount = 0;
        private selectedFolderCount = 0;
        private deRegFnFileSelect;
        private deRegFnFileUnselect;
        private deRegFnSharedLinkCreated;
        private deRegFnSharedLinkDeleted;
        private missingDocsFromFavorites = [];
        private missingFoldersFromFavorites = [];


        constructor() {
            this.deRegFnFileSelect = $rootScope.$on('fileSelect', (event, eventArgs) => {
                this.selectedItemCount += 1;

                if (eventArgs.type === 'file') {
                    this.selectedFileCount += 1;
                } else {
                    this.selectedFolderCount += 1;
                }
            });

            this.deRegFnFileUnselect = $rootScope.$on('fileUnselect', (event, eventArgs) => {
                // Ensure we never go negative
                if (this.selectedItemCount > 0) {
                    this.selectedItemCount -= 1;

                    if (eventArgs.type === 'file') {
                        this.selectedFileCount -= 1;
                    } else {
                        this.selectedFolderCount -= 1;
                    }
                }
            });

            this.deRegFnSharedLinkDeleted = $rootScope.$on(SignalProvider.ShareLink.Deleted, (event, permissionInfo) => {
                if (permissionInfo.resourceId === this.currentFolderId) {
                    this.currentFolderSharedLinkScope = null;
                    this.currentFolderSharedLinkUrl = null;
                }

            });

            this.deRegFnSharedLinkCreated = $rootScope.$on(SignalProvider.ShareLink.Created, (event, permissionInfo) => {
                if (permissionInfo.resourceId === this.currentFolderId) {
                    this.currentFolderSharedLinkScope = permissionInfo.linkScope;
                    this.currentFolderSharedLinkUrl = this.generateSharedLinkUrl(this.currentFolderSharedLinkScope, this.currentFolderId);
                }
            });
        }

        private setCurrentSharedByMeResourceFilter(startTime, endTime, resourceCategory) {
            this.currentSharedByMeResourceFilter.startTime = startTime;
            this.currentSharedByMeResourceFilter.endTime = endTime;
            this.currentSharedByMeResourceFilter.resourceCategory = resourceCategory;
        }

        private getBackingStoreFromType (type) {
            return type === 'file' ? this.selectedFilesSet : this.selectedFoldersSet;
        }

        private unwrapFilesAndFolders (result) {
            result = result || {};

            if (result.Folder) {
                return {
                    files: result.Folder.Documents || [],
                    folders: result.Folder.Folders || []
                };
            }

            return {
                files: result.Documents || [],
                folders: result.Folders || []
            };
        }

        private indexFiles (files) {
            return _.keyBy(files, (item) => {
                return item.Id;
            });
        }

        private indexFolders (folders) {
            return _.keyBy(folders, (item) => {
                return item.Id;
            });
        }

        private setFilesAndFolders (result) {

            var isMyDocuments = this.isMyDocumentsFolder();

            // filter if needed. Folders property is in the result for resource results
            // for others it is inside the Folder property.
            if(result && (result.Folders || (result.Folder  && result.Folder.Folders))){

                var folderToHide = isMyDocuments ? AppConstants.MEETING_ROOT_FOLDER_NAME : AppConstants.MEETING_FOLDER_PREFIX;

                // in my docs do not show Meeting-Files. In other views, do not show folders 
                // which start with the GUID below.
                if(result.Folders){
                    result.Folders = result.Folders.filter(folder =>  !folder.Name.startsWith(folderToHide));
                }
                else{

                    result.Folder.Folders = result.Folder.Folders.filter(folder =>  !folder.Name.startsWith(folderToHide));
                }
            }


            var unwrapped = this.unwrapFilesAndFolders(result);

            this.setFiles(unwrapped.files);
            this.setFolders(unwrapped.folders);
        }

        private appendSharedByMeFilesAndFolders(result) {
                var unwrapped = this.unwrapFilesAndFolders(result);
            if (unwrapped.files && unwrapped.files.length) {
                this.currentFolderFiles = (this.currentFolderFiles || []).concat(unwrapped.files.filter((file) => {
                    var idx = _.findIndex(this.currentFolderFiles, (file2) => {
                        return file2.Id === file.Id;
                    });
                    return idx === -1;
                }));
            }

            if (unwrapped.folders && unwrapped.folders.length) {
                this.currentFolderFolders = (this.currentFolderFolders || []).concat(unwrapped.folders.filter((file) => {
                    var idx = _.findIndex(this.currentFolderFolders, (file2) => {
                        return file2.Id === file.Id;
                    });
                    return idx === -1;
                }));
            }
        }

        private applyFolderPermissions (result, folderPermissions) {
            this.currentFolderRights = resourceService.processUserRightsFromRoles(folderPermissions.currentUserRoles);

            // Process delete & upload permission
            if (this.currentFolderRights.contains(this.permissions.CONTRIBUTE)) {
                this.currentFolderRights.add(this.permissions.UPLOAD);
            }
        }

        /**
         * We'll remap the Owner.ProfilePhotoMetadata.ProfilePhotoThumbnails to Owner.ProfilePhotoThumbnails object
         * for all folders and documents arrays
         */
        private processProfilePhotosOnFoldersAndDocs(foldersOrDocs) {
            if (!foldersOrDocs || !foldersOrDocs.length) {
                return;
            }
            foldersOrDocs.forEach((folderOrDoc) => {
                if (folderOrDoc.Owner && folderOrDoc.Owner.ProfilePhotoMetadata) {
                    folderOrDoc.Owner.ProfilePhotoThumbnails = folderOrDoc.Owner.ProfilePhotoMetadata.ProfilePhotoThumbnails;
                }
            });
        }

        private processGetFolderGetDocumentResponses(responses) {
            var result = {
                Folders : [],
                Documents : []
            };
            responses.forEach((response) => {
                var data = response.data;
                if (!data || !data.Metadata ||
                    //don't show recycled resources
                    data.Metadata.ResourceState === AppConstants.ResourceStatus.RESOURCE_STATUS_RECYCLED ||
                    data.Metadata.ResourceState === AppConstants.ResourceStatus.RESOURCE_STATUS_RECYCLING) {
                    return;
                }
                if (data.Metadata.LatestVersionMetadata) {
                    //it's a document response

                    //The service unit tests are checking for LatestVersionMetadata only, hence it's more reliable than VersionMetadata though
                    //they're the same now
                    data.Metadata.VersionMetadata = data.Metadata.LatestVersionMetadata;
                    //There are some places where we except the VersionId field on the VersionMetadata
                    data.Metadata.VersionMetadata.VersionId = data.Metadata.VersionMetadata.Id;

                    //Remap the ThumbnailUrlMap for the document preview images to show up in thumbnail view
                    data.Metadata.VersionMetadata.ThumbnailUrlMap = data.Metadata.VersionMetadata.Thumbnail;
                    result.Documents.push(data.Metadata);
                } else {
                    result.Folders.push(data.Metadata);
                }
                //Apply the view model flag that shows this as a favorite
                data.Metadata.isFavorite = true;
            });
            return result;
        }

        private getDocumentCreatorIds(docs) {
            return docs.map((doc) => {
                return doc.CreatorId;
            });
        }

        private loadFolderContentImpl(sortOptions, getAllPagesAndSortLocally) {

            var folderPromise, deferred = $q.defer();

            if (this.isInTrashRoot()) {
                folderPromise = fileService.getRecycleBinRoot(sortOptions, getAllPagesAndSortLocally);
            } else if(this.isInRecoveryRoot() && recoveryFolderId === null) {
                //User doesn't have recovery folder yet
                folderPromise = {};
            } else {
                folderPromise = fileService.getFolder(this.getCurrentFolder() || rootFolderId, sortOptions, getAllPagesAndSortLocally);
            }

            $q.all({
                folder: folderPromise,
                permissions: (this.isInRecoveryRoot())? {data:{}} : resourceService.describeResourcePermissions(this.getCurrentFolder(), ['PRINCIPALS'])
            }).then((results) => {
                // results.folder now contains the result of the folderPromise
                // results.permissions now contains the results of the permissions check.
                var folder = results.folder.Folder,
                    permissions = resourceService.processResourcePermissionResponse(results.permissions.data, userInfoContext),
                    promiseData = {} as any;

                // Process getFolder results
                if (folder) {

                    if (folder.Documents) {
                        this.processProfilePhotosOnFoldersAndDocs(folder.Documents);
                    }
                    if (folder.Folders) {
                        this.processProfilePhotosOnFoldersAndDocs(folder.Folders);
                    }

                    this.currentFolder = folder;
                    this.currentFolderPrincipals = permissions.principals;

                    this.inTrash = (
                        this.currentFolder.Metadata.Id === userInfoContext.RecycleBinFolderId ||
                        this.currentFolder.Metadata.ResourceState === this.resourceStatus.RESOURCE_STATUS_RECYCLED ||
                        this.currentFolder.Metadata.ResourceState === this.resourceStatus.RESOURCE_STATUS_RECYCLING);
                }

                this.setPagingMarker(results.folder.Marker);
                this.setFilesAndFolders(results.folder);
                this.setCurrentFolderSharedLinkScope(results.permissions.data);
                promiseData.folder = this.currentFolder;
                promiseData.hasMore = !!this.pagingMarker;

                // Process getPermissions results
                this.applyFolderPermissions(promiseData.folder, permissions);

                // Currently we don't support remove me from group share.
                angular.forEach(results.permissions.data.Principals, (principal) => {
                    if (getOwnerId(folder.Metadata) !== userInfoContext.Id && principal.Id === userInfoContext.Id) {
                        this.currentFolderRights.add(AppConstants.Permissions.REMOVE_FORM_SHARE);
                    }
                });

                if(getAllPagesAndSortLocally){
                    $log.info("Sorting fully paginated folder contents after load.");
                    this.sortLocally(sortOptions.sortBy, sortOptions.order);
                }

                deferred.resolve(promiseData);
            }, (reason) => {
                deferred.reject(reason);
            });

            return deferred.promise;
        }

        private getFolderCreatorIds(folders) {
            return folders.map((folder) => {
                return folder.CreatorId;
            });
        }

        private loadFavoritesContent(sortOptions) {
            return $q((resolve, reject) => {
                //Reset the missing docs/folders from favorites
                this.missingDocsFromFavorites = [];
                this.missingFoldersFromFavorites = [];

                FavoritesService.getCachedUserFavorites()
                .then((data) => {
                    var Folders = data.Folders,
                        Documents = data.Documents;

                    var folderPromises = Folders.map((id) => {
                        return fileService.getFolderNewApi(id).then(null, (response) => {
                            if (response.status === AppConstants.HttpStatuses.NOT_FOUND) {
                                this.missingFoldersFromFavorites.push(id);
                            }
                            //Don't reject the promise, we don't want the favorites page to error out becuase of one bad response
                            return response;
                        });
                    });
                    var docsPromises = Documents.map((id) => {
                        return DocumentService.getDocument(id, true).then(null, (response) => {
                            if (response.status === AppConstants.HttpStatuses.NOT_FOUND) {
                                this.missingDocsFromFavorites.push(id);
                            }
                            //Don't reject the promise, we don't want the favorites page to error out becuase of one bad response
                            return response;
                        });
                    });
                    $q.all(folderPromises.concat(docsPromises))
                    .then(processFolderDocsPromises.bind(this), processFolderDocsPromises.bind(this));

                    function processFolderDocsPromises(responses) {
                        var resultData = this.processGetFolderGetDocumentResponses(responses);
                        this.setFilesAndFolders(resultData);
                        var allIds = this.getFolderCreatorIds(resultData.Folders).concat(this.getDocumentCreatorIds(resultData.Documents));
                        UserCollectionService.getUserCollection(allIds).then(applyOwnerInfo.bind(this), applyOwnerInfo.bind(this));
                        function applyOwnerInfo(userCollection) {
                            userCollection.forEach((user) => {
                                resultData.Documents.forEach((doc) => {
                                    if (user.Id === doc.CreatorId) {
                                        doc.Owner = user;
                                    }
                                });
                                resultData.Folders.forEach((folder) => {
                                    if (user.Id === folder.CreatorId) {
                                        folder.Owner = user;
                                    }
                                });
                            });
                            this.sortLocally(sortOptions.sortBy, sortOptions.order).then(() => {
                                //Favorites results aren't paged
                                resolve({hasMore: false});
                            });
                        }
                    }

                }, reject);
            });
        }

        private resetContent () {
            this.currentFolderFolders = [];
            this.currentFolderFiles = [];
            this.currentFolderFolderMap = {};
            this.currentFolderFileMap = {};
            this.pagingMarker = null;
        }

        private isOwnerOfCurrentFolder () {
            ensureUserContext();

            return this.currentFolder.Metadata.Owner.Id === userInfoContext.Id;
        }

        private isMyDocumentsFolder() {
            ensureUserContext();

            if(this.currentFolder && this.currentFolder.Metadata){
                return this.currentFolder.Metadata.Id === userInfoContext.RootFolderId;
            }

            return false;
        }

        private fetchAllPagesAndSortLocally (sortBy, direction) {
            var sortOptions = {
                 sortBy: sortBy,
                 order: direction
             };
            return this.loadCurrentFolder(true, sortOptions);
        }

        private setCurrentFolderSharedLinkScope(resourcePermissionsResponse) {
            if (!resourcePermissionsResponse || !resourcePermissionsResponse.Principals) {
                return;
            }

            // Since all shared link are special entities in principals, iterate through all the principals
            // to see if there is any such special entities.
            resourcePermissionsResponse.Principals.forEach((principal) => {
                if (principal.Type === 'ANONYMOUS') {
                    this.currentFolderSharedLinkScope = AppConstants.SharedLinkScope.ANONYMOUS;
                    this.currentFolderSharedLinkUrl = this.generateSharedLinkUrl(this.currentFolderSharedLinkScope, this.currentFolderId);
                }
                if (principal.Type === 'ORGANIZATION') {
                    switch(principal.Roles[0].Role) {
                        case 'REVIEWER':
                        case 'VIEWER':
                            this.currentFolderSharedLinkScope = AppConstants.SharedLinkScope.SITE_VIEWER;
                            this.currentFolderSharedLinkUrl = this.generateSharedLinkUrl(this.currentFolderSharedLinkScope, this.currentFolderId);
                            break;
                        case 'CONTRIBUTOR':
                            this.currentFolderSharedLinkScope = AppConstants.SharedLinkScope.SITE_CONTRIBUTOR;
                            this.currentFolderSharedLinkUrl = this.generateSharedLinkUrl(this.currentFolderSharedLinkScope, this.currentFolderId);
                            break;
                        default:
                            break;
                    }
                }
            });
        }

        private sortLocally (sortBy, direction) {
            var
                folderComparator,
                fileComparator,
                nameFolderComparator,
                nameFileComparator,
                deferred,
                orderFactor,
                sharedSortLatencyMetric,
                Users = {};

            if(window.location.hash.includes('shared')){
                sharedSortLatencyMetric = $rootScope.metricServiceInstance.newLatencyMetric('Shared_Tab_Sort_Latency')
            }
            deferred = $q.defer();
            orderFactor = (direction.toUpperCase() === this.sortOrder.ASCENDING) ? this.SORT_DIRECTION_ASCENDING : this.SORT_DIRECTION_DESCENDING;

            nameFileComparator = (file1, file2) => {
                return (file1.VersionMetadata.Name.toLocaleLowerCase().localeCompare(file2.VersionMetadata.Name.toLocaleLowerCase())) * orderFactor;
            };

            nameFolderComparator = (folder1, folder2) => {
                return (folder1.Name.toLocaleLowerCase().localeCompare(folder2.Name.toLocaleLowerCase())) * orderFactor;
            };

            switch (sortBy) {
                case SortService.CONSTANTS.SORT_BY.NAME:
                    fileComparator = nameFileComparator;
                    folderComparator = nameFolderComparator;
                    break;

                case SortService.CONSTANTS.SORT_BY.OWNER:
                    fileComparator = (file1, file2) => {
                        var givenName1, surName1, givenName2, surName2, fullname1 = '', fullname2 = '';

                        if (Users[getOwnerId(file1)] && Users[getOwnerId(file2)]) {
                            fullname1 = $rootScope.getFullName(Users[getOwnerId(file1)]);
                            fullname2 = $rootScope.getFullName(Users[getOwnerId(file2)]);
                        }

                        return (fullname1.localeCompare(fullname2)) * orderFactor;
                    };

                    folderComparator = (folder1, folder2) => {
                        var givenName1, surName1, givenName2, surName2, fullname1 = '', fullname2 = '';

                        if (Users[getOwnerId(folder1)] && Users[getOwnerId(folder2)]) {
                            fullname1 = $rootScope.getFullName(Users[getOwnerId(folder1)]);
                            fullname2 = $rootScope.getFullName(Users[getOwnerId(folder2)]);
                        }

                        return (fullname1.localeCompare(fullname2)) * orderFactor;
                    };

                    break;

                case SortService.CONSTANTS.SORT_BY.DATE:
                    fileComparator = (file1, file2) => {
                        return (new Date(file1.VersionMetadata.Modified).getTime() - new Date(file2.VersionMetadata.Modified).getTime()) * orderFactor;
                    };

                    folderComparator = (folder1, folder2) => {
                        return (new Date(folder1.Modified).getTime() - new Date(folder2.Modified).getTime()) * orderFactor;
                    };

                    break;

                case SortService.CONSTANTS.SORT_BY.SIZE:
                    fileComparator = (file1, file2) => {
                        return (file1.VersionMetadata.Size - file2.VersionMetadata.Size) * orderFactor;
                    };

                    folderComparator = (folder1, folder2) => {
                        if(folder1.Size === undefined || folder2.Size === undefined )  {
                            return null;
                        }
                        return (folder1.Size - folder2.Size) * orderFactor;
                    };

                    break;

                case SortService.CONSTANTS.SORT_BY.TYPE:

                    var readableFileTypeCategoryFilter = $filter('humanReadableContentTypeName');

                    fileComparator = (file1, file2) => {
                        return ((readableFileTypeCategoryFilter(file1))
                                .localeCompare(readableFileTypeCategoryFilter(file2)) * orderFactor) ;
                    };

                    folderComparator = null;

                    break;

                case SortService.CONSTANTS.SORT_BY.FEEDBACK_REQUEST_TIMESTAMP:
                    fileComparator = (file1, file2) => {
                        if (file1.FeedbackRequestMetadata === null || file2.FeedbackRequestMetadata === null) {
                            return null;
                        }
                        return (new Date(file1.FeedbackRequestMetadata.FeedbackRequestTimestamp).getTime() - new Date(file2.FeedbackRequestMetadata.FeedbackRequestTimestamp).getTime()) * orderFactor;
                    };

                    folderComparator = null;
                    break;

                case SortService.CONSTANTS.SORT_BY.FEEDBACK_REQUEST_DEADLINE:
                    fileComparator = (file1, file2) => {
                        if (file1.FeedbackRequestMetadata === null || file2.FeedbackRequestMetadata === null) {
                            return null;
                        }
                        return (new Date(file1.FeedbackRequestMetadata.FeedbackRequestDeadline).getTime() - new Date(file2.FeedbackRequestMetadata.FeedbackRequestDeadline).getTime()) * orderFactor;
                    };

                    folderComparator = null;
                    break;
            }

            //We want to sort by the names first (with the same order factor) and then by the actual sort for consistent
            //experience.
            this.currentFolderFolders.sort(nameFolderComparator);
            this.currentFolderFiles.sort(nameFileComparator);

            if (sortBy !== SortService.CONSTANTS.SORT_BY.NAME) {

                if (sortBy === SortService.CONSTANTS.SORT_BY.OWNER) {
                    //If sorting by owner, wait until the UserCollectionService resolves all the owner names
                    var pendingUsersList = this.currentFolderFolders.map((obj) => {
                        return getOwnerId(obj);
                    }).concat(this.currentFolderFiles.map((obj) => {
                        return getOwnerId(obj);
                    }));
                    UserCollectionService.getUserCollection(pendingUsersList).then(populateUsersMapAndSort.bind(this), populateUsersMapAndSort.bind(this));
                } else {
                    doSorting.call(this);
                    deferred.resolve();
                }

            } else {
                //Nothing to do, sorting is already done
                deferred.resolve();
            }
            if(sharedSortLatencyMetric){
                sharedSortLatencyMetric.stopAndEmitMetric();
                sharedSortLatencyMetric = null;
            }
            return deferred.promise;

            function populateUsersMapAndSort(usersList) {
                usersList.forEach((User) => {
                    Users[User.Id] = User;
                });
                doSorting.call(this);
                deferred.resolve();
            }

            function doSorting() {
                if (folderComparator) {
                    this.currentFolderFolders.sort(folderComparator);
                }
                if (fileComparator) {
                    this.currentFolderFiles.sort(fileComparator);
                }
            }
        }

        private generateSharedLinkUrl(currentFolderSharedLinkScope, folderId) {
            if (!currentFolderSharedLinkScope) {
                return null;
            }
            var tempElement = document.createElement('a');
            tempElement.href = '#/folder/' + encodeURIComponent(folderId);
            return tempElement.href;
        }

        private clearSelectionData () {
            this.selectedFoldersSet.clear();
            this.selectedFilesSet.clear();

            this.selectedItemCount = 0;
            this.selectedFileCount = 0;
            this.selectedFolderCount = 0;
        }

        public destroy() {
            this.deRegFnFileSelect();
            this.deRegFnFileUnselect();
            this.deRegFnSharedLinkDeleted();
            this.deRegFnSharedLinkCreated();
        }

        public loadCurrentFolder (getAllPagesAndSortLocally, sortOptions) {

            if(!sortOptions){
                sortOptions = {
                 sortBy: this.currentSortBy,
                 order: this.currentSortDirection
             };
            }

            $log.debug("Loading Current Folder. getAllPagesAndSortLocally? " +
                    getAllPagesAndSortLocally + "; sortOptions : " +
                    JSON.stringify(sortOptions));

            if (this.inFavorites) {
                return this.loadFavoritesContent(sortOptions);
            }
            if (this.inSharedWithMeRoot) {
                return this.loadTaggedContentImpl(SystemTags.SHARED_WITH_ME, sortOptions, false);
            }

            if (this.inAwaitingMyFeedback) {
                return this.loadTaggedContentImpl(SystemTags.AWAITING_MY_FEEDBACK, sortOptions, false);
            }

            if (this.inSharedByMeRoot) {
                return this.loadSharedByMeContentImpl(this.currentSharedByMeResourceFilter, null);
            }

            if (this.inOutForReview) {
                return this.loadTaggedContentImpl(SystemTags.OUT_FOR_REVIEW, sortOptions, false);
            }

             return this.loadFolderContentImpl(sortOptions, getAllPagesAndSortLocally);
        }

        public setCurrentFolder (folderId, mode) {
            ensureUserContext();
            this.inFavorites = (mode === this.viewTypes.FAVORITES);
            this.inTrash = (mode === this.viewTypes.RECYCLE_TYPE);
            this.inSharedWithMeRoot = (mode === this.viewTypes.SHARED_TYPE);
            this.inSharedByMeRoot = (mode === this.viewTypes.SHARED_BY_ME_TYPE);
            this.inOutForReview = (mode === this.viewTypes.OUT_FOR_REVIEW_TYPE);
            this.inAwaitingMyFeedback = (mode === this.viewTypes.AWAITING_FEEDBACK_TYPE);
            this.inRecoveryFolderRoot = (mode === this.viewTypes.RECOVERY_TYPE);
            this.inNoteFolderRoot = (mode === this.viewTypes.NOTE_TYPE);

            switch (mode) {
                case this.viewTypes.RECYCLE_TYPE:
                    folderId = userInfoContext.RecycleBinFolderId;
                    break;
                case this.viewTypes.SHARED_TYPE:
                case this.viewTypes.SHARED_BY_ME_TYPE:
                case this.viewTypes.OUT_FOR_REVIEW_TYPE:
                case this.viewTypes.AWAITING_FEEDBACK_TYPE:
                case this.viewTypes.FAVORITES:
                case this.viewTypes.SEARCH_TYPE:
                    folderId = null;
                    break;
                case this.viewTypes.RECOVERY_TYPE:
                    folderId = userInfoContext.RecoveryFolderId;
                    break;
                case this.viewTypes.NOTE_TYPE:
                    folderId = userInfoContext.NoteInfo.NoteFolderId ;
                    break;
                default:
                    if (!folderId) {
                        folderId = userInfoContext.RootFolderId;
                    }
                    break;
            }

            // Clear selection if we're switching folder
            if (folderId !== this.currentFolderId) {
                this.currentFolderId = folderId;
                this.clearSelectionData();
                this.currentFolder = null;
                this.currentFolderRights = null;
            }
        }

        public getCurrentFolder () {
            return this.currentFolderId;
        }

        public getCurrentFolderInfo () {
            return this.currentFolder;
        }

        public setSortOptions (sortField, newSortDirection) {
            this.currentSortBy = sortField;
            this.currentSortDirection = angular.isUndefined(newSortDirection) ? this.sortOrder.ASCENDING : newSortDirection;
        }

        public isInTrashRoot () {
            return this.currentFolderId === userInfoContext.RecycleBinFolderId;
        }

        public isInTrash () {
            return this.inTrash;
        }

        public isInRecoveryRoot () {
          return this.currentFolderId === recoveryFolderId;
        }

        public isInSharedByMe () {
            return this.inSharedByMeRoot;
        }

        public isInNoteRoot () {
            return userInfoContext.NoteInfo && this.currentFolderId === userInfoContext.NoteInfo.NoteFolderId ;
        }

        public setRecoveryFolder(folderId){
          recoveryFolderId = angular.isUndefined(folderId)? null : folderId;
        }

        /*
        * @ngdoc method
        * @name FileManager#getCurrentFolderAndFileCount
        *
        * @description
        * Get the count of folders and files in current folder
        *
        */
        public getCurrentFolderAndFileCount () {
            return this.currentFolderFolders.length + this.currentFolderFiles.length;
        }

        /*
        * @ngdoc method
        * @name FileManager#markAsSelected
        *
        * @description
        * Marks the specified item as selected.
        *
        * @param {string} type The type of item to select: either 'file' or 'folder'.
        * @param {string} itemId The ID of the file/folder to mark for selection.
        *
        */
        public markAsSelected (type, itemId) {
            var backingStore = this.getBackingStoreFromType(type);

            // Ensure we don't add + notify if the item is already selected
            if (!backingStore.has(itemId)) {
                backingStore.add(itemId);
                $rootScope.$broadcast('fileSelect', {type: type, value: itemId});
            }
        }

        /*
        * @ngdoc method
        * @name FileManager#unselect
        *
        * @description
        * Marks the specified item as unselected.
        *
        * @param {string} type The type of item to unselect: either 'file' or 'folder'.
        * @param {string} itemId The ID of the file/folder to unselect.
        *
        */
        public unselect (type, itemId) {
            var backingStore = this.getBackingStoreFromType(type);

            // Only delete if the item was selected
            if (backingStore.has(itemId)) {
                backingStore.deleteItem(itemId);
                $rootScope.$broadcast('fileUnselect', {type: type, value: itemId});
            }
        }

        /*
        * @ngdoc method
        * @name FileManager#selectedFiles
        *
        * @description
        * Gets the selected file ID's.
        *
        * @returns {array} the ID's of the selected files.
        *
        */
        public selectedFiles(): string[] {
            return this.selectedFilesSet.entries();
        }

        /*
        * @ngdoc method
        * @name FileManager#selectedFolders
        *
        * @description
        * Gets the selected folder ID's.
        *
        * @returns {array} the ID's of the selected folders.
        *
        */
        public selectedFolders(): string[] {
            return this.selectedFoldersSet.entries();
        }

        /**
         * Returns `true` if the specified document/folder is selected; otherwise, `false`.
         */
        public isSelected (type, itemId) {
            var backingStore = this.getBackingStoreFromType(type);
            return backingStore.has(itemId);
        }

        /*
        * @ngdoc method
        * @name FileManager#isFileSelected
        *
        * @description
        * Checks if the specified file is selected.
        *
        * @param {string} fileId The ID of the file to check for selection.
        * @returns {boolean} `true` the specified file is selected; otherwise, `false`.
        *
        */
        public isFileSelected (fileId) {
          return this.isSelected('file', fileId);
        }

        /*
        * @ngdoc method
        * @name FileManager#isFolderSelected
        *
        * @description
        * Checks if the specified folder is selected.
        *
        * @param {string} folderId The ID of the folder to check for selection.
        * @returns {boolean} `true` the specified folder is selected; otherwise, `false`.
        *
        */
        public isFolderSelected (folderId) {
          return this.isSelected('folder', folderId);
        }

        public getTotalSelectedItemCount () {
            return this.selectedItemCount;
        }

        public getSelectedDocCount () {
            return this.selectedFileCount;
        }

        public getSelectedFolderCount () {
            return this.selectedFolderCount;
        }

        /*
        * @ngdoc method
        * @name FileManager#clearSelected
        *
        * @description
        * Clears all files and folders marked as selected.
        * That is, after this method is called, the selectedItemsCount will be 0.
        *
        */
        public clearSelected () {
            this.clearSelectionData();
            $rootScope.$broadcast('fileUnselectAll');
        }

        /*
        * @ngdoc method
        * @name FileManager#selectAll
        *
        * @description
        * select all folders and files that is in the view. The unshown paginated result will
        * not be selected.
        *
        */
        public selectAll () {
            this.getFolders().forEach((folder) => {
                this.markAsSelected('folder', folder.Id);
            });
            this.getFiles().forEach((file) => {
                this.markAsSelected('file', file.Id);
            });
            $rootScope.$broadcast('fileSelectAll');
        }

        public getFiles () {
            return this.currentFolderFiles;
        }

        public getFileIds () {
            var fileIds = [],
                numFiles = this.currentFolderFiles.length;

            for (var i = 0; i < numFiles; i++) {
                fileIds.push(this.currentFolderFiles[i].Id);
            }

            return fileIds;
        }

        public getMarker () {
            return this.pagingMarker;
        }

        public getFolders () {
            return this.currentFolderFolders;
        }

        public setFiles (files) {
            this.currentFolderFiles = files;
            this.currentFolderFileMap = this.indexFiles(this.currentFolderFiles);
        }

        public setFolders (folders) {
            this.currentFolderFolders = folders;
            this.currentFolderFolderMap = this.indexFolders(this.currentFolderFolders);
        }

        public appendFilesAndFolders (result) {
            var unwrapped = this.unwrapFilesAndFolders(result);
            this.currentFolderFiles.push.apply(this.currentFolderFiles, unwrapped.files);
            this.currentFolderFolders.push.apply(this.currentFolderFolders, unwrapped.folders);

            this.currentFolderFileMap = this.indexFiles(this.currentFolderFiles);
            this.currentFolderFolderMap = this.indexFolders(this.currentFolderFolders);
        }

        // Removes files and folders from the local currentFolderFiles and currentFolderFolders arrays.
        // It does *not* delete from the server.
        public removeFilesAndFolders(files, folders) {
            var deleteFileMap, deleteFolderMap;

            if (files && files.length) {
                deleteFileMap = _.keyBy(files);
                this.currentFolderFiles = _.reject(this.currentFolderFiles, (file) => {
                    return deleteFileMap.hasOwnProperty(file.Id);
                });

                this.currentFolderFileMap = this.indexFiles(this.currentFolderFiles);
            }

            if (folders && folders.length) {
                deleteFolderMap = _.keyBy(folders);
                this.currentFolderFolders = _.reject(this.currentFolderFolders, (folder) => {
                    return deleteFolderMap.hasOwnProperty(folder.Id);
                });

                this.currentFolderFolderMap = this.indexFolders(this.currentFolderFolders);
            }
        }

        public setPagingMarker (marker) {
            this.pagingMarker = marker;
        }


        public getMissingDocsAndFoldersFromFavorites() {
            return {
                Documents : this.missingDocsFromFavorites,
                Folders : this.missingFoldersFromFavorites
            };
        }

        /**
         * The following function is called in 2 flows
         *  1. Is the non cached flow. In that flow, useCachedResult will be false
         *  2. In the cached flow we always use the cached result if it's available
         */
        public loadTaggedContentImpl (tag, sortOptions, useCachedResult) {
            var deferred = $q.defer();
            var pendingPromise;
            if (!useCachedResult || !preFetchedSharedWithMeData) {
                if (tag === SystemTags.SHARED_WITH_ME && preFetchSharedWithMePendingPromise) {
                    pendingPromise = preFetchSharedWithMePendingPromise;
                } else {
                    pendingPromise = fileService.getByTag(tag);
                }
            } else if (tag === SystemTags.SHARED_WITH_ME) {
                pendingPromise = $q.when(preFetchedSharedWithMeData);
            }

            if (tag === SystemTags.SHARED_WITH_ME) {
                sortOptions = {
                    sortBy: this.currentSortBy,
                    order: this.currentSortDirection
                };
            }
            pendingPromise.then((results) => {
                if (tag === SystemTags.SHARED_WITH_ME) {
                    //We also need to store the latest value in the cache, otherwise the cache will be stale until the debounce fires
                    preFetchedSharedWithMeData = results;
                }
                this.setPagingMarker(false);
                if (results.Documents) {
                    this.processProfilePhotosOnFoldersAndDocs(results.Documents);
                }
                this.setFilesAndFolders(results);

                this.sortLocally(sortOptions.sortBy, sortOptions.order).then(() => {
                   //  Shared With Me results aren't paged
                    deferred.resolve({hasMore: false});
                });
            }, (reason) => {
                deferred.reject(reason);
            });

            return deferred.promise;
        }

        public loadSharedByMeContentImpl(sharedByMeFilter, marker) {
            var deferred = $q.defer(),
                  startTime = sharedByMeFilter ? sharedByMeFilter.startTime : null,
                  endTime = sharedByMeFilter ? sharedByMeFilter.endTime : null,
                  resourceCategory = sharedByMeFilter ? sharedByMeFilter.resourceCategory : null,
                  promise = fileService.getSharedByMeResources(startTime, endTime, resourceCategory, marker);

            this.setCurrentSharedByMeResourceFilter(startTime, endTime, resourceCategory);
            promise.then((results) => {

                this.setPagingMarker(results.Marker);
                if (results.Documents) {
                    this.processProfilePhotosOnFoldersAndDocs(results.Documents);
                }
                this.setFilesAndFolders(results);
                deferred.resolve({
                    hasMore: !!results.Marker
                });
            }, (reason) => {
                deferred.reject(reason);
            });
            return deferred.promise;
        }

        public loadMore () {
            var deferred = $q.defer();

            if (this.pagingMarker) {
                fileService.getFolder(this.getCurrentFolder(), {
                    sortBy: this.currentSortBy,
                    order: this.currentSortDirection,
                    marker: this.pagingMarker
                }).then((result) => {
                    this.pagingMarker = result.Marker;
                    var folder = result.Folder;
                    if (folder.Documents) {
                        this.processProfilePhotosOnFoldersAndDocs(folder.Documents);
                    }
                    if (folder.Folders) {
                        this.processProfilePhotosOnFoldersAndDocs(folder.Folders);
                    }
                    this.appendFilesAndFolders(result);
                    deferred.resolve({
                        hasMore: !!this.pagingMarker
                    });
                }, (reason) => {
                    deferred.reject(reason);
                });
            } else {
                deferred.reject();
            }
            return deferred.promise;
        }

        public loadMoreSharedByMeResources() {
            var deferred = $q.defer(),
                startTime = this.currentSharedByMeResourceFilter ? this.currentSharedByMeResourceFilter.startTime : null,
                endTime = this.currentSharedByMeResourceFilter ? this.currentSharedByMeResourceFilter.endTime : null,
                resourceCategory = this.currentSharedByMeResourceFilter ? this.currentSharedByMeResourceFilter.resourceCategory : null;

            if (this.pagingMarker) {
                fileService.getSharedByMeResources(startTime, endTime, resourceCategory, this.pagingMarker)
                .then((result) => {
                  this.pagingMarker = result.Marker;
                  if (result.Documents) {
                    this.processProfilePhotosOnFoldersAndDocs(result.Documents);
                  }
                  if (result.Folders) {
                    this.processProfilePhotosOnFoldersAndDocs(result.Folders);
                  }
                  this.appendSharedByMeFilesAndFolders(result);
                  deferred.resolve({
                    hasMore: !!this.pagingMarker
                  });
                }, (reason) => {
                  deferred.reject(reason);
                });
            } else {
              deferred.reject();
            }

            return deferred.promise;
        }

        public emptyTrash () {
            var deferred = $q.defer();

            fileService.emptyTrash()
            .then(() => {
                this.resetContent();
                deferred.resolve();
            }, (reason) => {
                deferred.reject(reason);
            });

            return deferred.promise;
        }

        /**
         * Returns a boolean indicating whether the current user has the specified right (`rightName`)
         * for the curent folder.
         */
        public hasRightForFolder (rightName) {
            return this.currentFolderRights && this.currentFolderRights.contains(rightName);
        }

        public getCurrentFolderPrincipals () {
            return this.currentFolderPrincipals;
        }

        public getCurrentFolderSharedLinkScope() {
            return this.currentFolderSharedLinkScope;
        }

        public getCurrentFolderSharedLinkUrl() {
            return this.currentFolderSharedLinkUrl;
        }

        public sort (sortBy, sortDirection) {
            this.setSortOptions(sortBy, sortDirection);

            // Determine whether we can sort client-side or not
            // (i.e. if it's a paged result and we don't have all the results, then we need server-side sort)
            if (this.pagingMarker) {
                // Go sort on the server
                $log.info('[FileManager] Paging marker, Fetching all pages to sort locally');
                return this.fetchAllPagesAndSortLocally(sortBy, sortDirection);
            }

            $log.info('[FileManager] No paging marker, sorting locally');
            return this.sortLocally(sortBy, sortDirection);
        }

        public getSelectedItems() {
            let selectedItems = [];
            selectedItems = selectedItems.concat(this.selectedFiles().map((fileId) => {
                let fileItem: ISelectedItem = {
                    type: AppConstants.ResourceTypes.FILE,
                    item: this.currentFolderFileMap[fileId]
                }
                return fileItem;
            }));

            selectedItems = selectedItems.concat(this.selectedFolders().map((folderId) => {
                let folderItem: ISelectedItem = {
                    type: AppConstants.ResourceTypes.FOLDER,
                    item: this.currentFolderFolderMap[folderId]
                }
                return folderItem;
            }));

            return selectedItems
        }

        public getRightClickMenuType() {
            if(this.getTotalSelectedItemCount() === 1) {
                if (this.selectedFiles().length > 0) {
                    return AppConstants.RightClickMenus.FILE_SELECTED;
                } else {
                    return AppConstants.RightClickMenus.FOLDER_SELECTED;
                }
            } else if (this.getTotalSelectedItemCount() > 1) {
                return AppConstants.RightClickMenus.MULTI_SELECTED;
            } else {
                return null;
            }
        }

        public getItemType(itemId: string) {
            if (this.currentFolderFileMap[itemId]) {
                return AppConstants.ResourceTypes.FILE;
            } else if (this.currentFolderFolderMap[itemId]) {
                return AppConstants.ResourceTypes.FOLDER;
            } else {
                return null;
            }
        }
    }

    return {
        FileManager : FileManager,
        setUserContext: setUserContext,
        preFetchSharedWithMe : preFetchSharedWithMe
    };
}
